export class XMLDiff {
  constructor(options = {}) {
    this.uniqueTags = new Set(options.uniqueTags || []);
  }

  calculatePatch(oldXML, newXML) {
    const parser = new DOMParser();
    const oldDoc = parser.parseFromString(oldXML, "text/xml");
    const newDoc = parser.parseFromString(newXML, "text/xml");
    const diffs = [];
    
    this._compareNodes(
      oldDoc.documentElement,
      newDoc.documentElement,
      diffs,
      this._getXPath(oldDoc.documentElement)
    );
    
    return diffs;
  }

  _compareNodes(oldNode, newNode, diffs, path) {
    // Обработка удаления узла
    if (!newNode && oldNode) {
      diffs.push({ action: 'remove', xpath: path });
      return;
    }

    // Обработка добавления нового узла
    if (!oldNode && newNode) {
      diffs.push({
        action: 'add',
        xpath: this._getParentXPath(newNode),
        value: this._serializeNode(newNode),
        position: 'append'
      });
      return;
    }

    // Проверка типа узла
    if (oldNode.nodeType !== newNode.nodeType) {
      this._replaceNode(oldNode, newNode, diffs, path);
      return;
    }

    // Сравнение в зависимости от типа узла
    switch (oldNode.nodeType) {
      case Node.ELEMENT_NODE:
        this._compareElements(oldNode, newNode, diffs, path);
        break;
      case Node.TEXT_NODE:
      case Node.CDATA_SECTION_NODE:
        this._compareTextContent(oldNode, newNode, diffs, path);
        break;
      case Node.ATTRIBUTE_NODE:
        this._compareAttributes(oldNode, newNode, diffs, path);
        break;
    }
  }

  _compareElements(oldElem, newElem, diffs, path) {
    // Проверка имени тега
    if (oldElem.tagName !== newElem.tagName) {
      this._replaceNode(oldElem, newElem, diffs, path);
      return;
    }

    // Сравнение атрибутов
    this._compareAttributeLists(oldElem, newElem, diffs, path);

    // Сравнение дочерних узлов
    this._compareChildNodes(oldElem, newElem, diffs, path);
  }

  _compareAttributeLists(oldElem, newElem, diffs, path) {
    const oldAttrs = Array.from(oldElem.attributes);
    const newAttrs = Array.from(newElem.attributes);
    const allNames = new Set([
      ...oldAttrs.map(a => a.name),
      ...newAttrs.map(a => a.name)
    ]);

    for (const name of allNames) {
      const oldAttr = oldAttrs.find(a => a.name === name);
      const newAttr = newAttrs.find(a => a.name === name);

      if (!oldAttr && newAttr) {
        diffs.push({ action: 'add-attr', xpath: path, name, value: newAttr.value });
      } else if (oldAttr && !newAttr) {
        diffs.push({ action: 'remove-attr', xpath: path, name });
      } else if (oldAttr.value !== newAttr.value) {
        diffs.push({ action: 'update-attr', xpath: path, name, value: this._serializeNode(newElem) }); //newAttr.value
      }
    }
  }

  _compareChildNodes(oldParent, newParent, diffs, parentPath) {
      // 1. Сравниваем дочерние элементы
      const oldElements = this._createChildMap(oldParent);
      const newElements = this._createChildMap(newParent);

      // Удаление элементов
      oldElements.forEach((oldChild, key) => {
          if (!newElements.has(key)) {
              diffs.push({ action: 'remove', xpath: this._getXPath(oldChild) });
          }
      });

      // Добавление/обновление элементов
      newElements.forEach((newChild, key) => {
          const oldChild = oldElements.get(key);
          const xpath = this._getXPath(newChild);

          if (!oldChild) {
              diffs.push({
                  action: 'add',
                  xpath: parentPath,
                  value: this._serializeNode(newChild)
              });
          } else {
              // Рекурсивное сравнение содержимого элемента
              this._compareNodes(oldChild, newChild, diffs, xpath);
              
              // 2. Сравниваем текстовые узлы внутри элемента
              this._compareInnerText(oldChild, newChild, diffs, xpath);
          }
      });
  }

  _compareInnerText(oldElem, newElem, diffs, path) {
      // Сравниваем только если у элемента есть текстовые узлы
      const oldText = this._getElementText(oldElem);
      const newText = this._getElementText(newElem);

      if (oldText !== newText) {
          diffs.push({
              action: 'update-text',
              xpath: `${path}`, ///text() - это я пока убрал, подумаем, надо ли
              value: newText
          });
      }
  }

  _getElementText(node) {
      return Array.from(node.childNodes)
          .filter(n => n.nodeType === Node.TEXT_NODE)
          .map(n => n.textContent.trim())
          .join(' ');
  }

  // Получаем все дочерние узлы (включая текстовые)
  _getChildNodes(node) {
    return node ? Array.from(node.childNodes) : [];
  }

  // Генерируем идентификатор узла для XPath
  _getNodeIdentifier(node, index) {
    if (!node) return `node()[${index + 1}]`;
    
    switch(node.nodeType) {
      case Node.ELEMENT_NODE:
        if (node.hasAttribute('id')) {
          return `${node.tagName}[@id='${node.getAttribute('id')}']`;
        }
        return `${node.tagName}[${index + 1}]`;
      case Node.TEXT_NODE:
        return `text()[${index + 1}]`;
      case Node.CDATA_SECTION_NODE:
        return `cdata()[${index + 1}]`;
      default:
        return `node()[${index + 1}]`;
    }
  }

  _createChildMap(parent) {
    const map = new Map();
    for (const child of parent.children) {
      const key = child.hasAttribute('id') 
        ? `id:${child.getAttribute('id')}` 
        : `tag:${child.tagName}:${Array.from(parent.children).indexOf(child)}`;
      map.set(key, child);
    }
    return map;
  }

  _getElementKey(node) {
    // Для элементов с id используем id как ключ
    if (node.hasAttribute('id')) {
      return `${node.tagName}#${node.getAttribute('id')}`;
    }
    
    // Для уникальных тегов используем имя тега
    if (this._isUniqueTag(node)) {
      return node.tagName;
    }

    // Для остальных - имя тега + индекс
    const siblings = Array.from(node.parentElement.children)
      .filter(n => n.tagName === node.tagName);
    const index = siblings.indexOf(node);
    return `${node.tagName}[${index}]`;
  }

  _isUniqueTag(node) {
    if (this.uniqueTags.has(node.tagName)) return true;
    
    const siblings = Array.from(node.parentElement.children);
    const sameTagSiblings = siblings.filter(n => n.tagName === node.tagName);
    return sameTagSiblings.length === 1;
  }

  _getXPath(node) {
    const parts = [];
    let current = node;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let selector = current.tagName;

      // Приоритет 1: Используем id если есть
      if (current.hasAttribute('id')) {
        selector += `[@id='${current.getAttribute('id')}']`;
        parts.unshift(selector);
        current = current.parentElement;
        continue;
      }

      // Приоритет 2: Проверяем уникальность тега
      const parent = current.parentElement;
      const siblings = parent ? Array.from(parent.children) : [];
      const sameTagSiblings = siblings.filter(n => n.tagName === current.tagName);

      if (this.uniqueTags.has(current.tagName) || sameTagSiblings.length === 1) {
        parts.unshift(selector);
      } else {
        // Приоритет 3: Используем индекс
        const index = sameTagSiblings.indexOf(current) + 1;
        selector += `[${index}]`;
        parts.unshift(selector);
      }

      current = parent;
    }

    return '/' + parts.join('/');
  }

  _getParentXPath(node) {
    return this._getXPath(node.parentElement);
  }

  _serializeNode(node) {
    const serializer = new XMLSerializer();
    return serializer.serializeToString(node);
  }

  _replaceNode(oldNode, newNode, diffs, path) {
    diffs.push({
      action: 'replace',
      xpath: path,
      oldValue: this._serializeNode(oldNode),
      newValue: this._serializeNode(newNode)
    });
  }

  _compareTextContent(oldNode, newNode, diffs, path) {
    const oldText = oldNode.textContent.trim();
    const newText = newNode.textContent.trim();

    if (oldText !== newText) {
      diffs.push({
        action: 'update-text',
        xpath: path,
        value: newText
      });
    }
  }

  _getElementChildren(node) {
    return Array.from(node.children).filter(n => 
      n.nodeType === Node.ELEMENT_NODE
    );
  }
}